import {CheckboxFilterList} from 'plugins/modules/filter/components/CheckboxFilterList';
import {FilterSearch} from 'plugins/modules/filter/components/FilterSearch';
import {FILTER_SHOW_SEARCH_THRESHOLD} from 'plugins/modules/filter/constants';
import {FilterType, IFilterFacet} from 'plugins/modules/filter/types';
import React, {FC, ChangeEventHandler, useCallback, useMemo, useState} from 'react';
import style from './style.module.scss';
import {isFilterWithCount} from 'plugins/modules/filter/helpers';

export interface IProps {
    filter: IFilterFacet;
    onApply: (id: string) => void;
    onDiscard: (id: string) => void;
    withSearch?: boolean;
}

export const CheckboxFilterBlock: FC<IProps> = ({filter, onApply, onDiscard, withSearch}) => {
    const [searchQuery, setSearchQuery] = useState<string>('');

    const isSearchInputVisible = useMemo<boolean>(() => {
        if (!withSearch) {
            return false;
        }

        const {type, variants} = filter;

        if (!variants) {
            return false;
        }

        const isShow = type === FilterType.CHECKBOX_OR || type === FilterType.CHECKBOX_AND;

        if (!isShow) {
            return false;
        }

        return FILTER_SHOW_SEARCH_THRESHOLD < variants.length;
    }, [filter, withSearch]);

    const handleApplyFilter = useCallback(
        (code: string, isChecked: boolean) => {
            if (isChecked) {
                onApply(code);

                return;
            }

            onDiscard(code);
        },
        [onApply, onDiscard],
    );

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleClear = useCallback(() => {
        setSearchQuery('');
    }, []);

    const filteredVariants = useMemo(() => {
        if (!filter.variants) {
            return [];
        }

        if (!isSearchInputVisible) {
            return filter.variants;
        }

        return filter.variants.filter(({title}) => {
            return String(title).toLowerCase().includes(searchQuery.toLowerCase());
        });
    }, [filter, isSearchInputVisible, searchQuery]);

    const withCount = isFilterWithCount(filter);

    return (
        <div>
            {isSearchInputVisible && (
                <div data-skeleton-item={true} className={style.search}>
                    <FilterSearch
                        isNothingFound={0 === filteredVariants.length}
                        onChange={handleInputChange}
                        onClear={handleClear}
                        placeholder={filter.title}
                    />
                </div>
            )}
            {0 < filteredVariants.length && (
                <CheckboxFilterList
                    filter={filter}
                    onChange={handleApplyFilter}
                    variants={filteredVariants}
                    withCount={withCount}
                />
            )}
        </div>
    );
};
